export interface CommodityProduct {
  name: string;
  description: string;
}

export interface CommodityDetail {
  slug: string;
  label: string;
  tagline: string;
  description: string;
  icon: string;
  heroImage: string;
  overviewTitle: string;
  overviewText: string;
  stats: { value: string; label: string }[];
  products: CommodityProduct[];
  approachTitle: string;
  approachText: string;
  approachPoints: string[];
  approachImage: string;
}

export const commodities: CommodityDetail[] = [
  {
    slug: 'energy-commodities',
    label: 'Energy Commodities',
    tagline: 'Powering the world through physical oil and gas markets',
    description: 'Crude oil, refined petroleum products, LPG and natural gas across global markets.',
    icon: 'ri-gas-station-line',
    heroImage:
      'https://public.readdy.ai/ai/img_res/e12494dcbbb56326c083cf82817d1391.jpg refinery industrial plant at sunset with orange glowing sky large storage tanks and pipes professional wide angle photography&width=1400&height=700&seq=energy101&orientation=landscape',
    overviewTitle: 'Our Core Trading Business',
    overviewText:
      'Energy commodities form the foundation of NRG Trading\'s operations. We actively trade crude oil, refined petroleum products, liquefied petroleum gas and natural gas across the world\'s most liquid and complex markets. Our traders combine deep market intelligence with access to a global logistics and storage network to source, move and deliver energy where it is needed most.',
    stats: [
      { value: '50+', label: 'Countries Supplied' },
      { value: '10+', label: 'Years Experience' },
    ],
    products: [
      { name: 'Crude Oil', description: 'A broad range of regional grades traded spot and forward.' },
      { name: 'Refined Products', description: 'Gasoline, diesel, jet fuel, fuel oil, naphtha and bitumen across all major refining hubs.' },
      { name: 'Liquefied Petroleum Gas', description: 'Propane and butane for industrial, residential and petrochemical end-markets globally.' },
      { name: 'Natural Gas', description: 'Pipeline gas and LNG across European and Asian markets, spot cargoes and term offtake.' },
      { name: 'Petrochemicals', description: 'Feedstock procurement and product distribution supporting downstream chemical producers.' },
      { name: 'Bunker Fuels', description: 'Marine fuel supply and blending services at key bunkering hubs worldwide.' },
    ],
    approachTitle: 'How We Trade Energy',
    approachText:
      'Our energy trading desk operates around the clock across time zones, running a fully integrated physical and financial book. We combine strong counterparty relationships with proprietary market intelligence to identify arbitrage opportunities and manage risk with precision.',
    approachPoints: [
      'Integrated physical and paper trading capabilities',
      'In-house freight and logistics coordination',
      'Robust compliance and risk management framework',
      'Long-standing relationships with national oil companies and refiners',
    ],
    approachImage:
      'https://readdy.ai/api/search-image?query=financial trading floor with large monitors showing oil price charts traders working at desks professional photography&width=900&height=560&seq=energy202&orientation=landscape',
  },
  {
    slug: 'energy-transition-metals',
    label: 'Energy Transition Metals',
    tagline: 'Critical minerals for a cleaner energy future',
    description: 'Lithium, cobalt, nickel and other critical metals powering the clean energy shift.',
    icon: 'ri-flashlight-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=lithium salt flat mining operation vast white salt lake aerial view industrial extraction facility South America professional photography&width=1400&height=700&seq=metals101&orientation=landscape',
    overviewTitle: 'Enabling the Energy Transition',
    overviewText:
      'As the global economy pivots toward electrification and clean energy, demand for critical minerals has surged. NRG Trading is strategically positioned to source and supply lithium, cobalt, nickel, manganese and other battery metals that underpin EV manufacturing, grid storage and renewable energy infrastructure.',
    stats: [
      { value: '12+', label: 'Metal Categories' },
      { value: '30+', label: 'Supply Origins' },
      { value: '5', label: 'Continent Reach' },
      { value: '2X', label: 'Demand Growth Forecast' },
    ],
    products: [
      { name: 'Lithium', description: 'Lithium carbonate and lithium hydroxide for battery cathode material manufacturers and chemical producers.' },
      { name: 'Cobalt', description: 'Refined cobalt metal and cobalt hydroxide precursor.' },
      { name: 'Nickel', description: 'LME-grade nickel, nickel sulphate and intermediate products for the EV battery supply chain.' },
      { name: 'Manganese', description: 'High-purity manganese ore and sulphate for battery and steel applications.' },
      { name: 'Rare Earth Elements', description: 'Neodymium, dysprosium and other REEs critical to magnet and motor production.' },
      { name: 'Graphite', description: 'Natural and synthetic graphite for anode materials in lithium-ion batteries.' },
    ],
    approachTitle: 'Our Role in the Battery Supply Chain',
    approachText:
      'NRG Trading bridges the gap between primary producers in emerging markets and industrial consumers internationally. We take pride in our ability to provide long-term supply security while maintaining the highest ESG standards in our sourcing practices.',
    approachPoints: [
      'Direct offtake agreements with mining operations',
      'ESG-aligned sourcing and due diligence',
      'Quality assurance and third-party assay services',
      'Financing structures tailored for producers and consumers',
      'Logistics expertise across landlocked and maritime routes',
    ],
    approachImage:
      'https://readdy.ai/api/search-image?query=electric vehicle battery manufacturing plant robotic assembly line clean modern industrial facility wide angle professional photography&width=900&height=560&seq=metals202&orientation=landscape',
  },
  {
    slug: 'soft-commodities',
    label: 'Soft Commodities',
    tagline: 'Agricultural products and biofuel feedstocks for a diversified energy mix',
    description: 'Agricultural products and biofuel feedstocks supporting energy diversification.',
    icon: 'ri-seedling-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=golden wheat field harvest time sunset sky combine harvester sweeping agricultural landscape warm amber light panoramic professional photography&width=1400&height=700&seq=soft101&orientation=landscape',
    overviewTitle: 'Agri-Commodities and Bioenergy',
    overviewText:
      'NRG Trading\'s soft commodities desk bridges traditional agricultural markets with the growing demand for sustainable energy feedstocks. We trade vegetable oils, grains, sugar and biofuel-grade feedstocks, supporting food producers and bioenergy manufacturers alike across global supply chains.',
    stats: [
      { value: '8+', label: 'Crop Categories' },
      { value: '25+', label: 'Origin Countries' },
      { value: '1MT+', label: 'Annual Volume' },
      { value: '40+', label: 'End Markets' },
    ],
    products: [
      { name: 'Vegetable Oils', description: 'Palm oil, soybean oil, rapeseed oil and sunflower oil for biodiesel and food-grade end-uses.' },
      { name: 'Grains', description: 'Wheat, corn and sorghum for industrial processing, animal feed and bioenergy conversion.' },
      { name: 'Sugar', description: 'Raw and refined sugar, as well as sugarcane ethanol for blending into fuel grades.' },
      { name: 'Biofuel Feedstocks', description: 'Used cooking oil, tallow and other waste fats for HVO and FAME biodiesel production.' },
      { name: 'Oilseeds', description: 'Soybeans, rapeseed and sunflower seed traded CIF and FOB across major port positions.' },
      { name: 'Cotton & Fibres', description: 'Agricultural fibres supporting the broader commodity portfolio and industrial applications.' },
    ],
    approachTitle: 'Trading Agri and Biofuel Markets',
    approachText:
      'Our soft commodities team operates with deep agronomic knowledge and strong origination networks across producing regions. We work closely with farmers, cooperatives, processors and end-users to structure trades that deliver value at every point in the supply chain.',
    approachPoints: [
      'Global origination network',
      'Quality assurance and pre-shipment inspection protocols',
      'Flexible delivery terms: FOB, CIF, and DAP arrangements',
      'Sustainability-linked procurement and carbon footprint tracking',
    ],
    approachImage:
      'https://readdy.ai/api/search-image?query=large grain storage silos agricultural terminal port bulk cargo vessel loading wide angle professional photography warm light&width=900&height=560&seq=soft202&orientation=landscape',
  },
  {
    slug: 'ores-concentrates',
    label: 'Ores & Concentrates',
    tagline: 'Mine-to-smelter supply chain solutions for base metal ores',
    description: 'Copper, zinc and lead concentrates sourced from mines across multiple continents.',
    icon: 'ri-earth-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=aerial view open pit copper mine arid landscape terraced benches mining trucks massive excavation dramatic earth tones panoramic professional photography&width=1400&height=700&seq=ores101&orientation=landscape',
    overviewTitle: 'International Concentrates Selling',
    overviewText:
      'NRG Trading operates across the full concentrates supply chain, sourcing copper, zinc and lead concentrates from mining operations on multiple continents and delivering them to smelters and refiners worldwide. Our expertise in assaying, logistics and payment structures makes us a trusted partner for both producers and processors.',
    stats: [
      { value: '20+', label: 'Mine Relationships' },
      { value: '15+', label: 'Smelter Partnerships' },
      { value: '4', label: 'Continents' },
      { value: '500K+', label: 'MT Annually' },
    ],
    products: [
      { name: 'Copper Concentrates', description: 'Bulk copper concentrates.' },
      { name: 'Zinc Concentrates', description: 'Zinc sulphide concentrates for smelters, spot and term offtake structures.' },
      { name: 'Lead Concentrates', description: 'Lead-bearing concentrates for smelter feed.' },
      { name: 'Cobalt Intermediates', description: 'Cobalt hydroxide and mixed hydroxide precipitate.' },
      { name: 'Nickel Concentrates', description: 'Nickel laterite and sulphide concentrates.' },
      { name: 'Polymetallic Ores', description: 'Complex multi-metal ores requiring specialist blending and downstream expertise.' },
    ],
    approachTitle: 'Managing Complexity in the Concentrates Market',
    approachText:
      'The concentrates market requires significant technical knowledge, financial capability and logistics expertise. NRG Trading brings all three, managing complex multi-shipment programs, providing pre-payment and streaming finance to producers, and navigating the intricate world of penalties, payable metals and treatment charges.',
    approachPoints: [],
    approachImage:
      'https://readdy.ai/api/search-image?query=copper smelter molten metal pouring industrial furnace metallurgical plant orange glow professional industrial photography wide angle&width=900&height=560&seq=ores202&orientation=landscape',
  },
  {
    slug: 'non-ferrous-metals',
    label: 'Non-Ferrous Metals',
    tagline: 'Physical and forward market trading of base and specialty metals',
    description: 'Aluminium, copper and zinc traded in physical and forward markets worldwide.',
    icon: 'ri-copper-diamond-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=polished copper cathode sheets stacked metal warehouse industrial lighting shiny red copper plates modern storage facility professional photography&width=1400&height=700&seq=nonferrous101&orientation=landscape',
    overviewTitle: 'Base Metals, Global Reach',
    overviewText:
      'NRG Trading\'s non-ferrous metals desk provides physical and structured trading solutions across aluminium, copper, zinc, tin and lead. We operate across spot, forward and term markets.',
    stats: [
      { value: '6+', label: 'Metal Grades' },
      { value: 'LME', label: 'Registered Operator' },
      { value: '200K+', label: 'MT Capacity' },
      { value: 'Global', label: 'Warehouse Access' },
    ],
    products: [
      { name: 'Aluminium', description: 'Primary aluminium ingots, billets and sows; aluminium alloys for automotive and packaging markets.' },
      { name: 'Copper', description: 'Copper cathodes, wire rod and copper scrap, traded globally.' },
      { name: 'Zinc', description: 'Special High Grade zinc.' },
      { name: 'Lead', description: 'Refined lead ingots for battery manufacturing and industrial applications.' },
      { name: 'Tin', description: 'Refined tin for solder, tinplate and specialty alloy applications.' },
      { name: 'Nickel', description: 'Full-plate nickel cathodes and briquettes for stainless steel and electroplating.' },
    ],
    approachTitle: 'Physical Metals with Financial Precision',
    approachText:
      'Our non-ferrous team combines physical market expertise with sophisticated financial hedging to deliver price certainty and supply continuity to our clients. We provide inventory financing, tolling arrangements and structured offtake solutions tailored to the needs of producers, consumers and traders.',
    approachPoints: [
      'Physical delivery across major global warehouse locations',
      'Inventory management and financing solutions',
      'Price risk management using options and structured products',
      'Deep relationships with primary producers and smelters',
    ],
    approachImage:
      'https://readdy.ai/api/search-image?query=aluminium smelter pot line orange glow molten aluminium large scale primary production industrial facility wide angle professional photography&width=900&height=560&seq=nonferrous202&orientation=landscape',
  },
  {
    slug: 'recycled-metals',
    label: 'Recycled Metals',
    tagline: 'Circular economy supply chains for secondary metals and scrap',
    description: 'Scrap and secondary metals supporting circular economy supply chains.',
    icon: 'ri-recycle-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=organized metal scrap recycling yard sorted aluminium copper materials clear sky industrial facility aerial panoramic professional photography&width=1400&height=700&seq=recycled101&orientation=landscape',
    overviewTitle: 'Driving the Circular Economy',
    overviewText:
      'Recycled metals are a cornerstone of sustainable industrial production. NRG Trading sources and trades ferrous and non-ferrous scrap, end-of-life material and secondary refined metals, connecting generators with consumers in a way that reduces energy use, CO₂ emissions and dependence on primary mining.',
    stats: [
      { value: '40%', label: 'Lower CO₂ vs Primary' },
      { value: '8+', label: 'Scrap Grades' },
      { value: '60+', label: 'Origin Markets' },
      { value: '1MT+', label: 'Annual Volume' },
    ],
    products: [
      { name: 'Aluminium Scrap', description: 'Old and new aluminium scrap.' },
      { name: 'Copper Scrap', description: 'Standard ISRI grades for wire mills and refiners.' },
      { name: 'Stainless Steel Scrap', description: 'Sorted and unsorted stainless grades.' },
      { name: 'Lead-Acid Battery Scrap', description: 'Whole and drained batteries.' },
      { name: 'Electronic Scrap', description: 'E-waste streams for precious metal and base metal recovery.' },
      { name: 'Zinc-Bearing Secondary', description: 'Hot dip galvanizing ash, zinc dross and skimmings for secondary zinc producers.' },
    ],
    approachTitle: 'Our Commitment to Sustainable Sourcing',
    approachText:
      'NRG Trading applies strict due diligence to all recycled metals procurement, ensuring full compliance with Basel Convention regulations, BIR standards and buyer specifications. We work closely with authorised treatment facilities, local authorities and end-users to build transparent, traceable supply chains.',
    approachPoints: [
      'Established relationships with authorised treatment facilities',
      'Third-party sorting, grading and container inspections',
      'ESG-aligned sourcing and transparent chain of custody',
      'Blending expertise to meet precise consumer specifications',
    ],
    approachImage:
      'https://readdy.ai/api/search-image?query=metal recycling plant conveyor belts sorting scrap metal industrial lighting secondary metals processing facility professional photography&width=900&height=560&seq=recycled202&orientation=landscape',
  },
  {
    slug: 'ferrous-metals',
    label: 'Ferrous Metals',
    tagline: 'Iron ore, steel and pig iron for global industrial markets',
    description: 'Iron ore, steel scrap and pig iron for industrial and construction markets.',
    icon: 'ri-hammer-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=electric arc furnace steel mill molten steel pouring dramatic orange white glow industrial plant wide angle professional photography&width=1400&height=700&seq=ferrous101&orientation=landscape',
    overviewTitle: 'Steel and Iron Markets',
    overviewText:
      'Ferrous metals are at the heart of global industrial output. NRG Trading trades iron ore fines and lumps, steel scrap, pig iron and direct reduced iron across markets in Asia, the Middle East, Europe and Africa. Our team has deep knowledge of integrated and electric arc furnace steelmaking supply chains.',
    stats: [
      { value: '2MT+', label: 'Annual Volume' },
      { value: '20+', label: 'Steel Mill Clients' },
      { value: '5', label: 'Origin Regions' },
      { value: '35+', label: 'Years Market Depth' },
    ],
    products: [
      { name: 'Iron Ore', description: 'Fine and lump iron ore, pellets and sinter feed from major producers.' },
      { name: 'Steel Scrap', description: 'Ferrous scrap grades for steelmakers worldwide.' },
      { name: 'Pig Iron', description: 'Basic and foundry pig iron.' },
      { name: 'Direct Reduced Iron', description: 'DRI for electric applications.' },
      { name: 'Billet & Slab', description: 'Semi-finished steel products.' },
      { name: 'Ferro-Alloys', description: 'Ferrosilicon, ferrochrome, ferromanganese and other alloying agents for steelmakers.' },
    ],
    approachTitle: 'Navigating the Global Steel Market',
    approachText:
      'The ferrous metals market is complex, cyclical and geopolitically sensitive. NRG Trading navigates this landscape with experience, relationships and a disciplined approach to risk. We support steelmakers with reliable, competitively priced raw material supply while managing price exposure through structured hedging.',
    approachPoints: [
      'Long-standing relationships with major iron ore miners',
      'Experienced Capesize and Panamax vessel chartering team',

      'Credit and political risk management across frontier markets',
    ],
    approachImage:
      'https://readdy.ai/api/search-image?query=cargo ship loading iron ore bulk export terminal conveyor systems industrial cranes port facility aerial professional photography clear sky&width=900&height=560&seq=ferrous202&orientation=landscape',
  },
];
